import { apiClient } from '../client';
import type { SaleResult } from '@never2busy/shared';
import type { Paginated, PaginationQuery } from './types';

export interface Sale {
  id: number;
  saleNumber: string | null;
  name: string;
  product: string | null;
  amount: string | null;
  probability: number | null;
  stage: string | null;
  result: SaleResult;
  saleDate: string | null;
  poNumber: string | null;
  accountId: number | null;
  personId: number | null;
  projectId: number | null;
  currencyId: number | null;
  assignedToId: number | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  version: number;
  account?: { id: number; name: string } | null;
  person?: { id: number; firstName: string | null; lastName: string } | null;
  project?: { id: number; name: string; projectNumber: string | null } | null;
  assignedTo?: { id: number; firstName: string; lastName: string } | null;
  currency?: { id: number; code: string } | null;
}

export interface SaleListQuery extends PaginationQuery {
  accountId?: number;
  personId?: number;
  projectId?: number;
  assignedToId?: number;
  stage?: string;
  result?: SaleResult;
  fromDate?: string;
  toDate?: string;
  keywordIds?: string;
}

export type SaleInput = Partial<Omit<Sale, 'id' | 'createdAt' | 'updatedAt' | 'version'>> & {
  name: string;
};

export const salesApi = {
  async list(q: SaleListQuery): Promise<Paginated<Sale>> {
    const { data } = await apiClient.get<Paginated<Sale>>('/sales', { params: q });
    return data;
  },
  async pipeline(assignedToId?: number): Promise<Sale[]> {
    const { data } = await apiClient.get<Sale[]>('/sales/pipeline', {
      params: assignedToId ? { assignedToId } : undefined,
    });
    return data;
  },
  async findOne(id: number): Promise<Sale> {
    const { data } = await apiClient.get<Sale>(`/sales/${id}`);
    return data;
  },
  async create(input: SaleInput): Promise<Sale> {
    const { data } = await apiClient.post<Sale>('/sales', input);
    return data;
  },
  async update(id: number, input: Partial<SaleInput>): Promise<Sale> {
    const { data } = await apiClient.patch<Sale>(`/sales/${id}`, input);
    return data;
  },
  async setStage(id: number, stage: string | null): Promise<Sale> {
    const { data } = await apiClient.patch<Sale>(`/sales/${id}/stage`, { stage });
    return data;
  },
  async setResult(id: number, result: SaleResult): Promise<Sale> {
    const { data } = await apiClient.patch<Sale>(`/sales/${id}/result`, { result });
    return data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`/sales/${id}`);
  },
};
